/**
 * HL Hero — empty state (no live HYPERLIQUID mainnet portfolio).
 *
 * Three onboarding stages:
 *   Stage 0 — hasAnyAgent=false   → "Set Up Your Agent" wizard CTA
 *   Stage 1 — hasAnyAgent + FREE  → "Unlock Live Trading" subscribe CTA
 *   Stage 2 — hasAnyAgent + paid  → "Ready to get real?" promote CTA
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PayDeeporbUSDC from '../PayDeeporbUSDC';

const shimmerStyle = {
  background: 'linear-gradient(135deg, #d303fc, #1c193d)',
  backgroundSize: '200% 200%',
  animation: 'hl-cta-shimmer 2.4s ease-in-out infinite',
};

const shimmerKeyframes = `
  @keyframes hl-cta-shimmer {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

export function HLHeroEmpty({ onUpgradeToLive, onCreateAgent, hasAnyAgent, walletAddress, planTier, config }) {
  const navigate = useNavigate();
  const [starterPrice, setStarterPrice] = useState(null);

  const isFreePlan = planTier?.toUpperCase() === 'FREE';

  useEffect(() => {
    if (!isFreePlan || !config?.apiBaseUrl) return;
    fetch(`${config.apiBaseUrl}/plans`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((plans) => {
        const starter = plans.find((p) => p.tier === 'STARTER');
        if (starter) setStarterPrice(starter.price_monthly_cents / 100);
      })
      .catch(() => {});
  }, [isFreePlan, config?.apiBaseUrl]);

  // ── Stage 0: no agent on any portfolio ──────────────────────────────────────
  if (!hasAnyAgent) {
    return (
      <>
      <style>{shimmerKeyframes}</style>
      <div className="border border-slate-700 rounded-lg p-5 sm:p-6 bg-gradient-to-br from-slate-800/80 to-slate-900 border-l-4 border-l-cyan-500">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-900/50 text-cyan-400 border border-cyan-700/50 font-semibold tracking-wide">
              STEP 1
            </span>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Set Up Your First AI Agent</h2>
          </div>
          {walletAddress && (
            <span className="text-xs text-slate-500 font-mono hidden sm:inline">
              {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
            </span>
          )}
        </div>

        <hr className="border-slate-700 border-dashed mb-4" />

        {/* Content */}
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Left: explanation */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white mb-2">Your portfolio needs an agent.</h3>
            <p className="text-sm text-slate-400 mb-3 leading-relaxed">
              The setup wizard walks you through naming your agent, choosing a research strategy,
              running your first market scan, and generating a trade recommendation —
              all in a few clicks.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
                &#10003; choose a strategy
              </span>
              <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
                &#10003; run first research
              </span>
              <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
                &#10003; get first recommendation
              </span>
            </div>
          </div>

          {/* Right: CTA */}
          <div className="flex flex-col gap-3 w-full md:w-56 flex-shrink-0">
            <button
              onClick={onCreateAgent}
              style={shimmerStyle}
              className="w-full px-4 py-2.5 text-white rounded-lg text-sm font-semibold"
            >
              Create Your Agent &rarr;
            </button>
            <p className="text-xs text-slate-500 text-center">free to set up &middot; no card required</p>
          </div>
        </div>
      </div>
      </>
    );
  }

  // ── Stage 1: has agent but on FREE plan ──────────────────────────────────────
  if (isFreePlan) {
    return (
      <div className="border border-slate-700 rounded-lg p-5 sm:p-6 bg-gradient-to-br from-slate-800/80 to-slate-900 border-l-4 border-l-amber-500">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900/50 text-yellow-400 border border-yellow-700/50 font-semibold">
              LOCKED
            </span>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Unlock Live Trading Now</h2>
          </div>
          {walletAddress && (
            <span className="text-xs text-slate-500 font-mono hidden sm:inline">
              {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
            </span>
          )}
        </div>

        <hr className="border-slate-700 border-dashed mb-4" />

        {/* Content */}
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Left: explanation */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white mb-2">Instantly start trading on Hyperliquid.</h3>
            <p className="text-sm text-slate-400 mb-3 leading-relaxed">
              Unlock live perpetual trading with <strong className="text-slate-300">Advanced AI research and Setups</strong>.
              Your agent analyzes markets, generates trade recommendations, and executes leveraged positions — all on Hyperliquid.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
                &#10003; Advanced AI agent research
              </span>
              <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
                &#10003; Live perpetual trading on Hyperliquid
              </span>
              <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
                &#10003; Graduate paper strategies to live
              </span>
            </div>
          </div>

          {/* Right: CTA */}
          <div className="flex flex-col gap-3 w-full md:w-56 flex-shrink-0">
            <div className="text-center">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Starter Plan</span>
              <div className="text-2xl font-bold text-white mt-0.5">
                {starterPrice != null ? `$${starterPrice}` : '—'}
                <span className="text-sm font-normal text-slate-400"> USDC/mo</span>
              </div>
            </div>
            {starterPrice != null ? (
              <PayDeeporbUSDC
                mode="upgrade"
                planTier="STARTER"
                billingPeriod="MONTHLY"
                amount={String(starterPrice)}
                config={config}
                buttonText="Subscribe to Starter →"
                onSuccess={() => window.location.reload()}
                onError={(err) => console.error('Subscription failed:', err)}
              />
            ) : (
              <button
                disabled
                className="w-full px-4 py-2.5 bg-amber-600/50 text-white rounded-lg text-sm font-medium cursor-not-allowed opacity-50"
              >
                Loading…
              </button>
            )}
            <button
              onClick={() => navigate('/plans')}
              className="w-full px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium transition-colors border border-slate-600 text-center"
            >
              see all plans &rarr;
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-700 rounded-lg p-5 sm:p-6 bg-gradient-to-br from-slate-800/80 to-slate-900 border-l-4 border-l-amber-500">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900/50 text-yellow-400 border border-yellow-700/50 font-semibold">
            PENDING
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Ready to get real?</h2>
          <span className="text-xs text-slate-500 hidden sm:inline">one per wallet &middot; Hyperliquid</span>
        </div>
        {walletAddress && (
          <span className="text-xs text-slate-500 font-mono hidden sm:inline">
            {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
          </span>
        )}
      </div>

      <hr className="border-slate-700 border-dashed mb-4" />

      {/* Content */}
      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Left: explanation */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-white mb-2">No live portfolio yet.</h3>
          <p className="text-sm text-slate-400 mb-3 leading-relaxed">
            Practice with paper portfolios below to tune a strategy you trust.
            When ready, connect Hyperliquid and <strong className="text-slate-300">graduate a paper strategy to live</strong> in
            one click — we'll copy your agent and config over, fresh start on balance.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
              &#10003; keep your agent
            </span>
            <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
              &#10003; keep research strategy
            </span>
            <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
              &#10003; keep prompts
            </span>
            <span className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-400 opacity-60">
              &#10007; not trade history
            </span>
          </div>
        </div>

        {/* Right: CTA */}
        <div className="flex flex-col gap-3 w-full md:w-56 flex-shrink-0">
          <style>{shimmerKeyframes}</style>
          <button
            onClick={onUpgradeToLive}
            style={shimmerStyle}
            className="w-full px-4 py-2.5 text-white rounded-lg text-sm font-semibold"
          >
            Promote To Live Portfolio &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}
